import { Icon, ICONS_ENUM, Typography } from '@sanlam-glacier/app-common/components';
import { StageStatus } from '../stage-status/StageAndStatus';
import styles from './stage-status-progress.module.scss';
import clsx from 'clsx';
import { STAGE_CONFIG } from 'modules/case-tracking/utils/case-stages-config';

type StageStatusProgressProps = {
  stage: number;
  stageDescription: string;
  status: string;
  isCancelled: boolean;
  isTablet?: boolean;
  lastModified: Date;
  cancellationReason?: string;
};

export const StageStatusProgress = ({
  stage,
  stageDescription,
  isCancelled,
  isTablet,
  lastModified,
  cancellationReason,
  status
}: StageStatusProgressProps) => {
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0] ?? '';
  };

  const isCancelledPlan = () => cancellationReason?.toLowerCase().includes('plan') ?? false;

  const getCurrentStageInfo = () => {
    if (isCancelled) {
      return {
        title: isCancelledPlan() ? `Plan cancelled` : `Instruction cancelled`,
        description: null
      };
    }
    return STAGE_CONFIG[stage as keyof typeof STAGE_CONFIG] || { title: '', description: '' };
  };

  const currentStageInfo = getCurrentStageInfo();

  return (
    <div className={styles.instructionProgress}>
      <Typography
        data-testid='statusProgressHeading'
        weight={Typography.WEIGHT.MEDIUM}
        variant={Typography.VARIANT.H4}
      >
        Instruction progress
      </Typography>

      <div className={styles.stageHeader}>
        <Typography
          color={Typography.COLOR.PRIMARY}
          weight={Typography.WEIGHT.MEDIUM}
          variant={Typography.VARIANT.B2}
          className={clsx(styles.stageTitle, isCancelled && styles.isCancelled)}
          data-testid='stageTitle'
        >
          {isCancelled && (
            <Icon
              data-testid='stageCancelledIcon'
              icon={ICONS_ENUM.ALERT_ERROR}
              className={styles.stageCircleCancelled}
            />
          )}
          {isCancelled ? currentStageInfo.title : `Stage ${stage}: ${currentStageInfo.title}`}
        </Typography>

        {currentStageInfo.description && (
          <Typography
            className={styles.stageDescription}
            data-testid={'stageDescription'}
            weight={Typography.WEIGHT.NORMAL}
            variant={Typography.VARIANT.B3}
            color={Typography.COLOR.SECONDARY}
          >
            {currentStageInfo.description}
          </Typography>
        )}
      </div>

      <StageStatus
        data-testid={'stageAndStatusProgress'}
        stage={stage}
        stageDescription={stageDescription}
        isCancelled={isCancelled}
        isTablet={isTablet}
        isProgressVersion={true}
      />

      <div className={styles.statusInfo}>
        <Typography
          data-testid={'statusText'}
          className={styles.statusText}
          weight={Typography.WEIGHT.NORMAL}
          variant={Typography.VARIANT.B3}
          color={Typography.COLOR.SECONDARY}
        >
          Status:{' '}
          {isCancelled ? (isCancelledPlan() ? 'Plan cancelled' : 'Instruction cancelled') : status}
        </Typography>

        {isCancelled && (
          <Typography
            data-testid={'statusReason'}
            className={styles.cancellationReason}
            weight={Typography.WEIGHT.NORMAL}
            variant={Typography.VARIANT.B3}
          >
            Reason: {cancellationReason || '-'}
          </Typography>
        )}
        <Typography
          data-testid={'instructionLastModified'}
          className={styles.lastModified}
          weight={Typography.WEIGHT.NORMAL}
          variant={Typography.VARIANT.B4}
        >
          Last modified: {formatDate(lastModified)}
        </Typography>
      </div>
    </div>
  );
};
